import { useState, memo, useEffect } from 'react';
import { DefaultCloseoutTypeKey } from './CloseoutTypes';
import { DEFAULT_CLOSEOUT_TYPE_OPTIONS } from './CloseoutTypes';
import { renderTable } from './closetoutHelpers';
import { TableStyleWrapper } from './TableStyleWrapper';
import {
  MenuItem,
  Select,
  Box,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Paper,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Create a memoized component to prevent re-renders
const TableContainer = memo(
  ({ selectedType, theme }: { selectedType: DefaultCloseoutTypeKey; theme: any }) => {
    // Each table instance manages its own isClearingFocus state
    const [isClearingFocus, setIsClearingFocus] = useState(false);

    const clearFocus = () => {
      setIsClearingFocus(true);
      setTimeout(() => setIsClearingFocus(false), 100);
    };

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TableStyleWrapper>
          {renderTable(selectedType, clearFocus, isClearingFocus)}
        </TableStyleWrapper>
      </Paper>
    );
  }
);

interface CloseoutSettingsProps {
  jumpto?: string; // The closeout type we want to set our Closeout Type to when the component mounts
  handleCloseWindow?: () => void;
  onLoaded?: () => void; // Callback when component is fully loaded
}

export const CloseoutSettings: React.FC<CloseoutSettingsProps> = ({
  handleCloseWindow,
  jumpto,
  onLoaded,
}) => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState<DefaultCloseoutTypeKey>('directlaborList');
  // Unique key to force full unmount/remount of the table component
  const [tableKey, setTableKey] = useState(0);

  // Set initial selected type based on jumpto prop
  useEffect(() => {
    if (jumpto) {
      // Only change if the jumpto value is different
      if (jumpto !== selectedType) {
        setSelectedType(jumpto as DefaultCloseoutTypeKey);
        // Reset table key to force remount with new type
        setTableKey(prev => prev + 1);
      }
    }
  }, [jumpto]);

  // Call onLoaded callback when component is mounted
  useEffect(() => {
    // Small delay to ensure rendering is complete
    const timer = setTimeout(() => {
      if (onLoaded) {
        onLoaded();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [onLoaded]);

  const handleChange = (e: any) => {
    const newValue = e.target.value as DefaultCloseoutTypeKey;
    // When changing types, increment the key to force a full remount
    setTableKey(prev => prev + 1);
    setSelectedType(newValue);
  };

  const handleClose = () => {
    if (handleCloseWindow) {
      handleCloseWindow();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: 3,
        paddingTop: 5, // Increased top padding to make room for the close button
        position: 'relative', // Added for positioning the close button
      }}
    >
      {/* Close button - only shown when handleCloseWindow is provided */}
      {handleCloseWindow && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
          }}
        >
          <Tooltip title="Close">
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: theme.palette.grey[500],
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <FormControl fullWidth>
        <InputLabel
          id="closeout-type-select-label"
          sx={{
            color: theme.palette.text.primary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        >
          Closeout Type
        </InputLabel>
        <Select
          labelId="closeout-type-select-label"
          value={selectedType}
          onChange={handleChange}
          label="Closeout Type"
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '& .MuiSelect-icon': {
              color: theme.palette.text.secondary,
            },
          }}
        >
          {DEFAULT_CLOSEOUT_TYPE_OPTIONS.map(opt => (
            <MenuItem
              key={opt.key}
              value={opt.key}
              sx={{
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.1),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                },
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText sx={{ color: theme.palette.text.secondary }}>
          Select the type of closeout to manage
        </FormHelperText>
      </FormControl>

      {/* Use key to force complete remount when the type changes */}
      <TableContainer key={tableKey} selectedType={selectedType} theme={theme} />
    </Box>
  );
};
